import React, { useState, useEffect } from 'react';
import billingService from '../../services/billingService';
import PlanCard from '../../components/billing/PlanCard';
import UsageProgress from '../../components/billing/UsageProgress';
import BillingHistory from '../../components/billing/BillingHistory';
import EnterpriseModal from '../../components/billing/EnterpriseModal';
import CheckoutModal from '../../components/billing/CheckoutModal';
import Dialog from '../../components/Dialog';
import { config } from '../../config/env';
import '../../components/billing/Billing.css';

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'UTC'
        }).format(date);
    } catch (e) {
        return dateString;
    }
};

const BillingSettings = () => {
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [currentBilling, setCurrentBilling] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
    const [dialogConfig, setDialogConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'confirm',
        variant: 'default',
        onConfirm: null
    });

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

    useEffect(() => {
        loadData();
        // if (config.PADDLE_PUBLIC_KEY) {
        //     billingService.initializePaddle(config.PADDLE_PUBLIC_KEY, (event) => {
        //         if (event.name === 'checkout.completed') {
        //             // Start polling for the plan change
        //             pollForUpdate();
        //         }
        //     });
        // }
    }, []);

    const pollForUpdate = async (maxAttempts = 10) => {
        setProcessing(true);
        let attempts = 0;
        const initialPlan = currentBilling?.plan;

        const checkUpdate = async () => {
            attempts++;
            try {
                const data = await billingService.getPlans();
                if (data.current?.plan !== initialPlan) {
                    setPlans(data.plans);
                    setCurrentBilling(data.current);
                    setProcessing(false);
                    setDialogConfig({
                        isOpen: true,
                        title: 'Success!',
                        message: `Your subscription has been successfully updated`,
                        type: 'alert',
                        variant: 'success'
                    });
                    return;
                }
            } catch (error) {
                console.error('Polling for update failed:', error);
            }

            if (attempts < maxAttempts) {
                setTimeout(checkUpdate, 2000); // Poll every 2 seconds
            } else {
                setProcessing(false);
                setDialogConfig({
                    isOpen: true,
                    title: 'Update Pending',
                    message: 'Your payment was successful, but your account update is still being processed. It should reflect in a few moments.',
                    type: 'alert',
                    variant: 'info'
                });
            }
        };

        checkUpdate();
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await billingService.getPlans();
            setPlans(data.plans);
            setCurrentBilling(data.current);
        } catch (error) {
            console.error('Failed to load billing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = (plan) => {
        setSelectedPlan(plan);
        setIsCheckoutOpen(true);
    };

    const handleFinalizeCheckout = async (planId, cycle, couponCode) => {
        try {
            setProcessing(true);
            const { checkoutUrl, transactionId } = await billingService.createCheckout(planId, cycle, couponCode);
            billingService.openCheckout(transactionId, checkoutUrl);
            setIsCheckoutOpen(false);
        } catch (error) {
            console.error('Upgrade failed:', error);
            throw error; // Let the modal handle the error display
        } finally {
            setProcessing(false);
        }
    };

    const handleEnterpriseSubmit = async (formData) => {
        await billingService.contactEnterprise(formData);
    };

    const confirmCancel = async () => {
        try {
            setProcessing(true);
            await billingService.cancelSubscription();
            await loadData(); // Reload to show cancelled status
        } catch (error) {
            console.error('Cancellation failed:', error);
            setDialogConfig({
                isOpen: true,
                title: 'Cancellation Error',
                message: 'Failed to cancel subscription.',
                type: 'alert',
                variant: 'danger'
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleCancel = () => {
        setDialogConfig({
            isOpen: true,
            title: 'Cancel Subscription',
            message: 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of the billing period.',
            type: 'confirm',
            variant: 'danger',
            confirmText: 'Yes, Cancel Subscription',
            cancelText: 'Keep My Plan',
            onConfirm: confirmCancel
        });
    };

    if (loading) {
        return <div className="billing-container">Loading billing information...</div>;
    }

    return (
        <div className="billing-container">
            <div className="billing-header">
                <h2>Subscription & Billing</h2>
                <p>Manage your plan and usage limits</p>
            </div>

            {/* Current Usage Section */}
            {currentBilling && (
                <div className="usage-section">
                    <h3>Current Usage</h3>
                    <div className="usage-grid">
                        <UsageProgress
                            label="Execution Minutes"
                            used={currentBilling.usageStats?.executionMinutesThisMonth || 0}
                            total={currentBilling.quotas?.executionMinutesPerMonth}
                            unit="mins"
                        />
                        <UsageProgress
                            label="Apps"
                            used={currentBilling.usageStats?.appsCount || 0}
                            total={currentBilling.quotas?.maxApps}
                        />
                        <UsageProgress
                            label="Team Members"
                            used={currentBilling.usageStats?.membersCount || 0}
                            total={currentBilling.quotas?.maxTeamMembers}
                        />
                    </div>

                    {currentBilling.plan !== 'free' && currentBilling.currentPeriodEnd && !currentBilling.cancelAtPeriodEnd && (
                        <div className="billing-info-row" style={{ marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                            <strong>Period:</strong> {formatDate(currentBilling.currentPeriodStart)} — {formatDate(currentBilling.currentPeriodEnd)} (UTC)
                        </div>
                    )}

                    {currentBilling.cancelAtPeriodEnd && (
                        <div className="alert alert-warning" style={{ marginTop: '20px' }}>
                            Your subscription has been cancelled and will end on {formatDate(currentBilling.currentPeriodEnd)}.
                        </div>
                    )}

                    {currentBilling.plan !== 'free' && !currentBilling.cancelAtPeriodEnd && (
                        <div style={{ marginTop: '20px' }}>
                            <button className="btn btn-danger" onClick={handleCancel} disabled={processing}>
                                Cancel Subscription
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Plans Section */}
            <div className="plans-section">
                <div className="plans-header">
                    <h3>Available Plans</h3>
                    <div className="billing-toggle-container">
                        <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={billingCycle === 'annual'}
                                onChange={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className={billingCycle === 'annual' ? 'active' : ''}>
                            Annual <span className="savings-badge">Save up to 17%</span>
                        </span>
                    </div>
                </div>
                <div className="plans-grid">
                    {plans.map(plan => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            billingCycle={billingCycle}
                            currentPlan={currentBilling}
                            onUpgrade={handleUpgrade}
                            onContactSales={() => setShowEnterpriseModal(true)}
                            isLoading={processing}
                        />
                    ))}
                </div>
            </div>

            {/* Billing History Section */}
            <BillingHistory />

            <EnterpriseModal
                isOpen={showEnterpriseModal}
                onClose={() => setShowEnterpriseModal(false)}
                onSubmit={handleEnterpriseSubmit}
            />

            <Dialog
                isOpen={dialogConfig.isOpen}
                onClose={() => setDialogConfig(prev => ({ ...prev, isOpen: false }))}
                title={dialogConfig.title}
                message={dialogConfig.message}
                type={dialogConfig.type}
                variant={dialogConfig.variant}
                confirmText={dialogConfig.confirmText}
                cancelText={dialogConfig.cancelText}
                onConfirm={dialogConfig.onConfirm}
            />

            {selectedPlan && (
                <CheckoutModal
                    isOpen={isCheckoutOpen}
                    onClose={() => setIsCheckoutOpen(false)}
                    plan={selectedPlan}
                    billingCycle={billingCycle}
                    onFinalize={handleFinalizeCheckout}
                    isProcessing={processing}
                />
            )}
        </div>
    );
};

export default BillingSettings;
